import GroupWorkIcon from "@mui/icons-material/GroupWork";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams, GroupParams } from "containers/TermFormContainer";
import { showsDefaultChannelConfiguration } from "containers/TermFormContainer/utils";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";
import ChannelBaseColumnsRecap from "../ChannelBaseColumnsRecap";
import ChannelsMilestonesRecap from "../ChannelsMilestonesRecap";
import FormatOverridesRecap from "../FormatOverridesRecap";
import FormulaScopeChip from "../FormulaScopeChip";
import { groupChannelFormulas, uniqueFormatOverrides } from "../formatTypeRecap";
import GroupMilestonesRecap from "../GroupMilestonesRecap";

interface IGroupRecap {
    /**
     * Group data.
     */
    group: GroupParams;
    /**
     * Names of the channels within the group.
     */
    groupChannelsNames: (string | undefined)[];
    /**
     * Data of channels within group.
     */
    groupChannels: ChannelParams[];
    /**
     * All existing channels for this company.
     */
    channels: IChannel[];
    formatTypeNamesById?: ReadonlyMap<number, string>;
}

// Group section within the Term Recap
const GroupRecap = ({
    group,
    groupChannels,
    groupChannelsNames,
    channels,
    formatTypeNamesById = new Map(),
}: IGroupRecap) => {
    const [t] = useTranslation();
    const channelById = new Map(channels.map((item) => [item.id, item]));
    const defaultRows = groupChannels.filter((row) => row.formatTypeId == null);
    const showSyncedDefaultFormula = showsDefaultChannelConfiguration(defaultRows[0]);
    const baseChannel = defaultRows[0] ?? groupChannels[0];
    const baseChannelData = baseChannel ? channelById.get(baseChannel.channelId) : undefined;
    const groupHasMilestones =
        !!baseChannelData?.hasMilestones ||
        (group?.synced ? (group?.data?.length ?? 0) > 1 : false) ||
        groupChannels.some((channel) => {
            const fullChannel = channelById.get(channel.channelId);
            return !!fullChannel?.hasMilestones || (channel?.data?.length ?? 0) > 1;
        });
    const syncedFormatOverrides = uniqueFormatOverrides(groupChannels);
    const showSyncedConfig = !group?.synced || showSyncedDefaultFormula || syncedFormatOverrides.length > 0;
    const formulasByChannelId = groupChannelFormulas(groupChannels);
    const seenChannelIds = new Set<number>();
    const uniqueDefaultNames: (string | undefined)[] = [];
    for (const row of defaultRows) {
        if (seenChannelIds.has(row.channelId)) continue;
        seenChannelIds.add(row.channelId);
        uniqueDefaultNames.push(row.channelName ?? channelById.get(row.channelId)?.name);
    }
    const displayedChannelNames = uniqueDefaultNames.length > 0 ? uniqueDefaultNames : groupChannelsNames;

    return (
        <div className="group-details" key={`group-${group.id}`}>
            <div className="group-title">
                <GroupWorkIcon sx={{ color: variables["foreground"], fontSize: 20 }} />
                <div className="group-name">
                    <span style={{ color: variables["foreground"] }}>{group.name}</span>
                    {group?.synced && (
                        <span className="group-channel-names" style={{ color: variables["foreground"] }}>
                            {displayedChannelNames?.join(", ")}
                        </span>
                    )}
                </div>
            </div>
            {showSyncedConfig && (
                <div className="channel-body">
                    <div className="channel-rail" aria-hidden />
                    <div className="channel-config">
                        {group?.synced && showSyncedDefaultFormula && syncedFormatOverrides.length > 0 && (
                            <FormulaScopeChip variant="default" label={t("ConnectTitle.DefaultFormula")} />
                        )}
                        {(!group?.synced || showSyncedDefaultFormula) && (
                            <ChannelBaseColumnsRecap
                                channel={baseChannel}
                                fullChannelData={baseChannelData}
                                hasMilestones={!!groupHasMilestones || !!baseChannelData?.hasMilestones}
                            />
                        )}
                        {group?.synced && showSyncedDefaultFormula && (
                            <GroupMilestonesRecap
                                chosenGroup={group}
                                hasMilestones={group?.data?.length > 1}
                                baseChannel={baseChannel}
                                fullChannelData={baseChannelData}
                            />
                        )}
                        {group?.synced && (
                            <FormatOverridesRecap
                                overrides={syncedFormatOverrides}
                                fullChannelData={baseChannelData}
                                formatTypeNamesById={formatTypeNamesById}
                            />
                        )}
                    </div>
                </div>
            )}

            {!group?.synced && groupChannels && (
                <div className="group-channels">
                    {[...formulasByChannelId.entries()].map(([channelId, formulas], index) => {
                        const fullChannelData = channelById.get(channelId);
                        const defaultChannel = formulas.defaultRow;
                        const showChannelDefault = !!defaultChannel && showsDefaultChannelConfiguration(defaultChannel);
                        return (
                            <div className="group-channel-info" key={`group-channel-${channelId}`}>
                                <span className="channel-name">
                                    {index + 1}. {fullChannelData?.name}
                                </span>
                                {showChannelDefault && (
                                    <>
                                        {formulas.overrides.length > 0 && (
                                            <FormulaScopeChip
                                                variant="default"
                                                label={t("ConnectTitle.DefaultFormula")}
                                            />
                                        )}
                                        <ChannelsMilestonesRecap
                                            chosenChannel={defaultChannel}
                                            fullChannelData={fullChannelData}
                                            hasMilestones={
                                                defaultChannel?.data?.length ? defaultChannel.data.length > 1 : false
                                            }
                                        />
                                    </>
                                )}
                                <FormatOverridesRecap
                                    overrides={formulas.overrides}
                                    fullChannelData={fullChannelData}
                                    formatTypeNamesById={formatTypeNamesById}
                                    showBaseColumns={false}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default GroupRecap;
